import React, { ChangeEvent, useMemo, useState } from 'react';
import usePagination from '../../hooks/usePagination/usePagination';

import { CSSTransition } from 'react-transition-group';
import { ChevronArrowDownIcon } from '../../assets/icons/ChevronArrowDownIcon';
import { ChevronArrowLeftIcon } from '../../assets/icons/ChevronArrowLeftIcon';
import { ChevronArrowRightIcon } from '../../assets/icons/ChevronArrowRightIcon';
import { ListDotIcon } from '../../assets/icons/ListDotIcon';
import { ThreeDots } from '../../assets/icons/ThreeDots';
import { ThreeDotsLoader } from '../../assets/icons/ThreeDotsLoader';
import useScreenSize from '../../hooks/useScreenSize/useScreenSize';
import { getMergedClassNames } from '../../utils/HTMLutils';
import Col from '../col/Col';
import DropdownItem from '../dropdownItem/DropdownItem';
import DropdownMenu from '../dropdownMenu/DropdownMenu';
import IconButton from '../iconButton/IconButton';
import Row from '../row/Row';

import { ScreenSize } from '../../libraries/ScreenSizes';
import './Pagination.scss';
const rootClassName = 'pagination-component';

type onChangeType = (event: ChangeEvent<unknown>, value: number) => void;

type defaultLabel = {
  from: number;
  to: number;
  count: number;
};

interface PaginationProps {
  onPageChange: onChangeType;
  totalPages: number;
  disabled?: boolean;
  loading?: boolean;
  maxLength?: number;
  currentPage?: number;
  siblingCount?: boolean | number;
  boundaryCount?: number;
  hideNextButton?: boolean;
  hidePrevButton?: boolean;
  countPages?: {
    rows: number;
    totalItems: number;
  };
  rowsPerPage?: {
    options: number[];
    rows: number;
    onChange: onChangeType;
  };
}

const Pagination: React.FC<PaginationProps> = (props) => {
  const { size, rankedSize } = useScreenSize();
  const [dropdownOpened, setDropdownOpened] = useState<boolean>(false);

  const {
    siblingCount = props.maxLength ? 0 : 1,
    boundaryCount = 1,
    currentPage,
    maxLength,
    hideNextButton,
    hidePrevButton,
    totalPages,
    onPageChange,
    disabled,
    loading,
    countPages,
    rowsPerPage,
  } = props;

  function validatePaginationProps(props: PaginationProps) {
    if (
      props.siblingCount !== false &&
      props.siblingCount !== undefined &&
      props.maxLength !== undefined
    ) {
      throw new Error(
        'Erro: siblingCount e maxLength não podem ser utilizadas juntas'
      );
    }
  }

  validatePaginationProps(props);

  const { items, pageCurrent } = usePagination({
    count: countPages
      ? Math.floor(
          countPages.totalItems % countPages.rows === 0
            ? countPages.totalItems / countPages.rows
            : countPages.totalItems / countPages.rows + 1
        )
      : totalPages,
    onChange: (e, v) => onPageChange(e, v),
    disabled: disabled,
    hideNextButton: hideNextButton,
    hidePrevButton: hidePrevButton,
    siblingCount: siblingCount,
    boundaryCount: boundaryCount,
    currentPage: currentPage,
    maxLength: maxLength,
  });

  const [rows, setRows] = useState<number | string | undefined>(
    countPages?.rows || rowsPerPage?.rows
  );

  const lastPage = items[items.length - 2].page;

  const defaultLabelDisplayedRows = ({ from, to, count }: defaultLabel) => {
    return `${from}-${to} de ${
      count !== -1 ? `${count} itens` : `maior que ${to}`
    }`;
  };

  const alignCenter = useMemo(() => {
    if (size === 'xs' && (rowsPerPage || countPages)) {
      return true;
    } else if (size === 'sm' && rowsPerPage && countPages) {
      return true;
    } else if (!rowsPerPage && !countPages) {
      return true;
    }

    return false;
  }, [rowsPerPage, countPages, size]);

  const alignEnd = useMemo(() => {
    if (size === 'sm' && rowsPerPage && countPages) {
      return false;
    } else if (rankedSize >= ScreenSize.sm && (rowsPerPage || countPages)) {
      return true;
    }
    return false;
  }, [size, rowsPerPage, countPages, rankedSize]);

  return (
    <Row
      className={getMergedClassNames([
        rootClassName,
        countPages ? 'hasLeftItem' : '',
        countPages || rowsPerPage ? 'oneOf' : '',
      ])}
    >
      {countPages || rowsPerPage ? (
        <Col
          noPadding
          cols={
            rowsPerPage && countPages
              ? [12, 12, 6, 6, 6]
              : rowsPerPage || countPages
              ? [12, 6, 6, 6, 6]
              : [12, 12, 12, 12, 12]
          }
        >
          {countPages || rowsPerPage ? (
            <div
              className={getMergedClassNames([
                'paginationLeftContainer',
                'nunito',
                countPages && rowsPerPage
                  ? 'both'
                  : countPages || rowsPerPage
                  ? 'oneOf'
                  : '',
              ])}
            >
              {countPages ? (
                <div>
                  <p>
                    {defaultLabelDisplayedRows({
                      from:
                        countPages.rows === 0
                          ? 0
                          : countPages.rows * (pageCurrent ?? 1) -
                            countPages.rows +
                            1,
                      to: Number(
                        pageCurrent === lastPage
                          ? countPages.totalItems
                          : countPages.rows * (pageCurrent ?? 1)
                      ),
                      count:
                        countPages.totalItems === -1
                          ? -1
                          : countPages.totalItems,
                    })}
                  </p>
                </div>
              ) : undefined}
              {rowsPerPage ? (
                <div>
                  <div className='linesPerPage'>
                    {countPages ? (
                      rankedSize >= ScreenSize.md ? (
                        <span>
                          <ListDotIcon />
                        </span>
                      ) : (
                        ''
                      )
                    ) : undefined}
                    <div
                      className={getMergedClassNames([
                        'wrapper',
                        loading || disabled ? 'disabled' : '',
                      ])}
                    >
                      <p>Linhas por página:</p>
                      <DropdownMenu
                        opened={!loading && !disabled && dropdownOpened}
                        setOpened={setDropdownOpened}
                        closeOnOutClick
                        content={
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                            }}
                            tabIndex={loading || disabled ? -1 : 0}
                          >
                            {rowsPerPage &&
                              rowsPerPage.options.map(
                                (value, index: number) => {
                                  return (
                                    <DropdownItem
                                      key={index}
                                      label={String(value)}
                                      disabled={loading}
                                      onClick={(e) => {
                                        setDropdownOpened(false);
                                        setRows(String(value));
                                        rowsPerPage.onChange(
                                          e,
                                          value as number
                                        );
                                      }}
                                    />
                                  );
                                }
                              )}
                          </div>
                        }
                      >
                        <div
                          onClick={() => setDropdownOpened(!dropdownOpened)}
                          className={getMergedClassNames([
                            'drop',
                            dropdownOpened ? 'active' : '',
                          ])}
                        >
                          <p className={'nunito'}>{rowsPerPage && rows} </p>
                          <ChevronArrowDownIcon />
                        </div>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ) : undefined}
            </div>
          ) : undefined}
        </Col>
      ) : undefined}
      <Col
        noPadding
        cols={
          rowsPerPage && countPages
            ? [12, 12, 6, 6, 6]
            : rowsPerPage || countPages
            ? [12, 6, 6, 6, 6]
            : [12, 12, 12, 12, 12]
        }
        centralized={alignCenter}
        end={alignEnd}
      >
        <div>
          {items.map(({ page, type, selected, ...item }, index) => {
            let children = null;

            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
              children = (
                <li
                  key={index}
                  className={getMergedClassNames([
                    'paginationItem',
                    'dots',
                    disabled ? 'disabled' : '',
                  ])}
                >
                  <ThreeDots />
                </li>
              );
            } else if (type === 'page') {
              children = (
                <li
                  key={index}
                  className={getMergedClassNames([
                    'paginationItem',
                    disabled ? 'disabled' : '',
                    selected ? 'selected' : '',
                    !loading ? 'nonLoader' : '',
                  ])}
                  tabIndex={disabled ? -1 : 0}
                  {...(item as object)}
                >
                  {page}
                </li>
              );
            } else if (type === 'voltar') {
              children = (
                <div className={'arrows'} aria-label={'previous page'}>
                  <IconButton
                    icon={<ChevronArrowLeftIcon />}
                    tabIndex={disabled ? -1 : 0}
                    {...(item as object)}
                  />
                </div>
              );
            } else if (type === 'próximo') {
              children = (
                <div className={'arrows'} aria-label={'previous page'}>
                  <IconButton
                    icon={<ChevronArrowRightIcon />}
                    tabIndex={disabled ? -1 : 0}
                    {...(item as object)}
                  />
                </div>
              );
            }

            return (
              <ul
                key={index}
                className={getMergedClassNames([
                  'paginationContainer',
                  loading ? 'hidden' : '',
                ])}
              >
                {children}
              </ul>
            );
          })}
          <CSSTransition
            in={loading}
            timeout={300}
            unmountOnExit
            classNames={{
              enter: 'enter',
              enterActive: 'enterActive',
              exit: 'exit',
              exitActive: 'exitActive',
              exitDone: 'exitDone',
            }}
          >
            <div className='paginationContainerLoader'>
              <ThreeDotsLoader size={2} />
            </div>
          </CSSTransition>
        </div>
      </Col>
    </Row>
  );
};

export default Pagination;
